import { unprocessedConfig } from 'Templates/templates';

/**
 * Resolves the store identity and server URLs needed early in startup.
 *
 * Keep this module isolated to `Templates/templates` and window globals.
 * Importing `Models/uiConfig/*` here would recreate the
 * `requestConfig -> diagnostics -> analytics -> requestConfig` cycle.
 */

const useDevServer = window.Convermax?.useDevServer;

// Ignore non-string store IDs so URL building never uses invalid values.
const configStoreId = typeof unprocessedConfig.storeId === 'string' ? unprocessedConfig.storeId : '';

interface ServerConfiguration {
  storeId: string;
  isLocalEnv: boolean;
  serverUrl: string;
  devServerUrl?: string;
  backupServerUrl: string;
}

function deriveServerConfiguration(): ServerConfiguration {
  // `currentScript` is missing in Jest and other non-DOM hosts.
  const { currentScript } = window.document;
  const jsScriptDomain = currentScript instanceof HTMLScriptElement ? currentScript.src : undefined;

  if (!jsScriptDomain) {
    return {
      storeId: configStoreId,
      isLocalEnv: false,
      serverUrl: `https://${configStoreId}.myconvermax.com`,
      backupServerUrl: `https://backup.convermax.com/${configStoreId}`,
    };
  }

  const isSearchableJsScriptDomain = jsScriptDomain.includes('.myconvermax.com');
  const isLocalEnv = jsScriptDomain.includes('localhost.convermax.dev:3000');

  const domainOrigin = new URL(jsScriptDomain).origin;
  const extractedStoreId = domainOrigin.replace('https://', '').replace('.myconvermax.com', '');
  const storeId = isSearchableJsScriptDomain ? extractedStoreId : configStoreId;

  const devServerUrl = `https://localhost.convermax.dev/${storeId}`;
  const backupServerUrl = useDevServer ? devServerUrl : `https://backup.convermax.com/${storeId}`;

  const serverUrl = useDevServer
    ? devServerUrl
    : isSearchableJsScriptDomain
      ? domainOrigin
      : isLocalEnv
        ? `https://${configStoreId}.myconvermax.com`
        : `https://client.convermax.com/${configStoreId}`;

  return {
    storeId,
    isLocalEnv,
    serverUrl,
    devServerUrl,
    backupServerUrl,
  };
}

const serverConfiguration = deriveServerConfiguration();

export const { storeId, isLocalEnv, serverUrl, backupServerUrl } = serverConfiguration;
export { useDevServer };
export default serverConfiguration;
